import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  CodeLabel,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  ItemPicker,
  Modal,
  Select,
  Spinner,
  Table,
} from '../../components'
import type { PickedItem, TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpcMessage } from '../../lib/trpcMessages'
import { MIRROR_REFETCH_MS, trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles } from '../admin/AdminPage'
import { SPECIAL_SCHEDULE_OPTIONS, isSpecialScheduleKind } from './planning'
import type { SpecialScheduleKind } from './planning'
import { useConfigurationRefresh } from './useConfigurationRefresh'

type SpecialScheduleRow = RouterOutputs['configuration']['listSpecialSchedule']['recipes'][number]

/**
 * COCCION BAJA: the recipes that stay out of the day plan because they cook
 * overnight or over the weekend. The name and the group are the mirror's; the
 * kind is the only thing kept here, and it is changed in place.
 */
export const SpecialSchedulePage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const refreshStatus = useConfigurationRefresh()

  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<SpecialScheduleRow | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const recipesQuery = trpc.configuration.listSpecialSchedule.useQuery(undefined, { refetchInterval: MIRROR_REFETCH_MS })

  const afterWrite = (): void => {
    setActionError(null)
    void utils.configuration.listSpecialSchedule.invalidate()
    refreshStatus()
  }

  const setKind = trpc.configuration.setSpecialSchedule.useMutation({
    onSuccess: afterWrite,
    onError: (error) => setActionError(trpcMessage(error)),
  })
  const remove = trpc.configuration.removeSpecialSchedule.useMutation({
    onSuccess: () => {
      setRemoving(null)
      afterWrite()
    },
    onError: (error) => {
      setRemoving(null)
      setActionError(trpcMessage(error))
    },
  })

  const columns: Array<TableColumn<SpecialScheduleRow>> = [
    {
      key: 'recipe',
      header: 'Receta',
      render: (row) => <CodeLabel code={row.code} title={row.item.title} known={row.item.known} />,
    },
    {
      key: 'group',
      header: 'Grupo',
      hideOnMobile: true,
      render: (row) => row.item.groupTitle ?? <span className={styles['admin-page__muted']}>—</span>,
    },
    {
      key: 'kind',
      header: 'Tipo',
      render: (row) => (
        <Select
          label={`Tipo de ${row.code}`}
          hideLabel
          options={SPECIAL_SCHEDULE_OPTIONS}
          value={row.kind}
          disabled={isReadOnly || setKind.isPending}
          onChange={(event) => {
            const { value } = event.target
            if (isSpecialScheduleKind(value) && value !== row.kind) {
              setActionError(null)
              setKind.mutate({ code: row.code, kind: value })
            }
          }}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (row) => (
        <Button
          variant="danger"
          disabled={isReadOnly}
          onClick={() => setRemoving(row)}
          aria-label={`Quitar ${row.code}`}
          label="Quitar"
        />
      ),
    },
  ]

  let body: ReactElement
  if (recipesQuery.isPending) {
    body = <Spinner label="Cargando recetas…" />
  } else if (recipesQuery.isError) {
    body = <ErrorState error={recipesQuery.error} onRetry={() => void recipesQuery.refetch()} />
  } else if (recipesQuery.data.recipes.length === 0) {
    body = <EmptyState title="Ninguna receta de cocción baja" message="Todas las recetas van al plan del día." />
  } else {
    body = (
      <>
        <FreshnessBadge freshness={recipesQuery.data.freshness} />
        <Table columns={columns} rows={recipesQuery.data.recipes} rowKey={(row) => row.code} caption="Recetas de cocción baja" />
      </>
    )
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Cocción baja"
        intro="Recetas que no van al plan del día porque se cocinan de noche o el fin de semana."
        actions={<Button disabled={isReadOnly} onClick={() => setAdding(true)} label="Añadir receta" />}
      />
      {isReadOnly && <ReadOnlyNote />}
      {actionError !== null && <p role="alert" className={styles['admin-page__error']}>{actionError}</p>}

      {body}

      {adding && (
        <AddRecipeDialog
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            afterWrite()
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        variant="danger"
        title="Quitar de cocción baja"
        message={removing === null ? '' : `${removing.code} volverá a planificarse en el plan del día desde el próximo plan.`}
        confirmLabel="Quitar"
        isLoading={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing !== null) {
            remove.mutate({ code: removing.code })
          }
        }}
      />
    </section>
  )
}

type AddRecipeDialogProps = {
  onClose: () => void
  onSaved: () => void
}

const AddRecipeDialog = ({ onClose, onSaved }: AddRecipeDialogProps): ReactElement => {
  const [recipe, setRecipe] = useState<PickedItem | null>(null)
  const [kind, setKind] = useState<SpecialScheduleKind | ''>('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const save = trpc.configuration.setSpecialSchedule.useMutation({
    onSuccess: onSaved,
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  return (
    <Modal open title="Añadir receta de cocción baja" onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (recipe !== null && kind !== '') {
            setErrorMessage(null)
            save.mutate({ code: recipe.code, kind })
          }
        }}
      >
        <ItemPicker
          label="Receta"
          selected={recipe}
          onSelect={setRecipe}
          hint="Si ya está en la lista, se le cambia el tipo."
        />
        <Select
          label="Tipo"
          placeholder="Elige un tipo…"
          options={SPECIAL_SCHEDULE_OPTIONS}
          value={kind}
          onChange={(event) => {
            const { value } = event.target
            setKind(isSpecialScheduleKind(value) ? value : '')
          }}
        />
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending} label="Cancelar" />
          <Button type="submit" disabled={save.isPending || recipe === null || kind === ''} label="Añadir" />
        </div>
      </form>
    </Modal>
  )
}
